import { apiResolverApi, RouteResult } from "@gymflow/api";
import {
  infiniteQueryOptions,
  QueryKey,
  useQuery,
} from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import { api } from "libs/api/src/lib/apiv2/api";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, LabeledForm, SearchInput } from "../components/atoms";
import { OptionType } from "../components/atoms/base/components/OptionsList/components/OptionItem";
import { FormInput } from "../components/atoms/form";
import { FormPasswordInput } from "../components/atoms/form/FormPasswordInput";
import { ToggleModeButtonNew } from "../components/pages/Leads/ToggleModeButtonNew";
import { rememberMeRouteIdKey } from "../providers/authentication/RememberMeRouteIdKey";
import { useAuthentication } from "../providers/authentication/useAuthentication";
import { resetTailwindColors } from "../providers/settings/resetTailwindColors";
import { ToastContext } from "../providers/ToastProvider/context";
import { RouteFeature } from "../routes";

const loginSchema = z.object({
  email: z.string().email("portal.login.validation.notAnEmail"),
  password: z.string().min(1, "portal.login.validation.passwordRequired"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function Welcome() {
  const { t } = useTranslation();
  const [route, setRoute] = useState<OptionType<RouteResult>[]>([]);
  const [userType, setUserType] = useState<"staff" | "member">("staff");
  const { data: serverDetails } = useQuery({
    queryKey: ["serverDetails", route[0]?.value.routeId],
    queryFn: () => apiResolverApi.getServerByRouteId(route![0].value.routeId),
    enabled: !!route[0]?.value.routeId,
  });
  const { toast } = useContext(ToastContext);
  const auth = useAuthentication({ serverDetails, api, userType: "staff" });
  const history = useHistory();
  const handleSubmit = useCallback(
    async (values: LoginFormValues) => {
      const response = await auth.login(values.email, values.password);
      if (!response.success) {
        toast({
          message: t("portal.errorCodes.generic"),
          description: {
            WRONG_CREDENTIALS: t("portal.login.validation.wrongCredentials"),
            WRONG_USER_TYPE: t("portal.login.validation.wrongUserType"),
            WRONG_CLUB: t("portal.login.validation.wrongClub"),
          }[response.error],
          intent: "error",
        });
        return;
      }

      if (!route[0]?.value.routeId) return;

      history.push({
        pathname: `${route[0].value.routeId}/staff`,
        search: "",
      });
    },
    [auth, history, route, t, toast],
  );
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: toFormikValidationSchema(loginSchema),
    onSubmit: handleSubmit,
  });
  useEffect(() => {
    const lastRouteId = localStorage.getItem(rememberMeRouteIdKey);
    if (lastRouteId && window.location.pathname === "/") {
      window.location.href = `${lastRouteId}/staff`;
    }
  }, []);
  const errorCount = Object.keys(formik.errors).filter(
    (key) =>
      formik.touched[key as keyof typeof formik.touched] &&
      formik.errors[key as keyof typeof formik.errors],
  ).length;

  useEffect(() => {
    resetTailwindColors();
  }, []);

  return (
    <div className="relative flex size-full flex-col items-center justify-center">
      <div className="absolute left-[-25vw] top-[-25vw] size-[50vw]">
        <div className="bg-secondary-500 size-full rounded-full opacity-50 blur-[25vw]" />
      </div>

      <div className="absolute bottom-[-25vw] right-[-25vw] size-[50vw]">
        <div className="bg-secondary-500 size-full rounded-full opacity-50 blur-[25vw]" />
      </div>

      <div className="z-10 flex size-full max-w-sm flex-col items-center justify-center gap-12 overflow-y-auto p-4 py-10 max-lg:justify-between ">
        <div className="flex w-full flex-col items-center gap-4">
          <div className="bg-gymflow-logo flex min-h-12 w-full bg-contain bg-center bg-no-repeat" />
          <div className="flex flex-col items-center gap-1 pt-8">
            <div className="text-2xl font-bold text-gray-950">
              {t("pages.login.header")}
            </div>
            <div className="text-base font-medium text-gray-500">
              {t("pages.login.headerDescription")}
            </div>
          </div>
          <motion.div
            animate={{
              height:
                userType === "staff"
                  ? `${23.375 + errorCount * 1.5}rem`
                  : "11rem",
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.1,
              ease: "linear",
              delay: userType === "staff" ? 0 : 0.2,
            }}
            className="bg-gray-0 flex w-full flex-col gap-4 rounded-[1.25rem] border border-gray-200 p-6 shadow-sm transition-all"
          >
            <ToggleModeButtonNew
              options={
                [
                  { label: t("pages.login.staff"), value: "staff" },
                  { label: t("pages.login.member"), value: "member" },
                ] as const
              }
              value={userType}
              onChange={(value) => setUserType(value)}
              className="min-h-11"
            />
            <LabeledForm label={t("pages.login.selectClub")}>
              <SearchInput<RouteResult>
                isMulti={false}
                value={route}
                onChange={(value) => setRoute(value)}
                dataFetchingQuery={(search: string) =>
                  infiniteQueryOptions({
                    queryKey: ["findRoutes", search] as QueryKey,
                    queryFn: async ({ pageParam }) =>
                      apiResolverApi.findRoutes({
                        search,
                        page: pageParam,
                        limit: 10,
                      }),
                    select: (data) => ({
                      ...data,
                      pages: data.pages.map(
                        (r) =>
                          r.results.map((r) => ({
                            label: r.displayName,
                            value: r,
                            id: r.routeId,
                          })) satisfies OptionType<RouteResult>[],
                      ),
                    }),
                    getNextPageParam: (lastPage, _, lastPageParam) =>
                      lastPage.hasMore ? lastPageParam + 1 : undefined,
                    initialPageParam: 0,
                  })
                }
              />
            </LabeledForm>
            <AnimatePresence mode="wait">
              {userType === "staff" && (
                <motion.div
                  key="staff"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{
                    duration: 0.2,
                    delay: 0.3,
                    exit: { duration: 0.2, delay: 0 },
                  }}
                  className="flex flex-col gap-4"
                >
                  <FormikProvider value={formik}>
                    <FormInput<LoginFormValues>
                      name="email"
                      type="email"
                      label={t("pages.login.email")}
                      placeholder={t("pages.login.enterEmail")}
                    />
                    <FormPasswordInput<LoginFormValues>
                      name="password"
                      label={t("pages.login.password")}
                      placeholder={t("pages.login.enterPassword")}
                    />
                  </FormikProvider>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{
                      duration: 0.2,
                      delay: 0.4,
                      exit: { duration: 0.2, delay: 0 },
                    }}
                    className="flex w-full items-center justify-center"
                  >
                    <Button
                      className="size-fit px-2 py-0"
                      size="small"
                      disabled={!serverDetails}
                      intent="link-secondary"
                      tooltip={
                        serverDetails?.urls.auth
                          ? undefined
                          : t("pages.login.forgotPasswordTooltip")
                      }
                      href={
                        serverDetails?.urls.auth
                          ? `${serverDetails?.urls.auth}/realms/${serverDetails?.authRealm}/login-actions/reset-credentials?client_id=gymflow-app`
                          : undefined
                      }
                      target="_blank"
                    >
                      {t("pages.login.forgotPassword")}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
        {userType === "staff" ? (
          <Button
            intent="secondary"
            className="w-fit px-20 max-lg:w-full"
            disabled={!formik.isValid || !serverDetails}
            onClick={async () => await formik.submitForm()}
          >
            {t("pages.login.signIn")}
          </Button>
        ) : (
          <Button
            intent="secondary"
            className="w-fit px-20 max-lg:w-full"
            disabled={!route[0]}
            link={
              route[0]
                ? `${route[0].value.routeId}/site${RouteFeature.SiteLogin}`
                : undefined
            }
            target="_blank"
          >
            {t("pages.login.signInMember")}
          </Button>
        )}
      </div>
    </div>
  );
}
